import * as React from 'react';

import { useHiveWorkingDocument } from '@gitbook/hive-react';
import { flex, TextInput, type TextInputRef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingIndicator, Pill } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { ContentEditorContext } from '../contexts';
import { createDrawingFromMermaid, type DrawingDocumentData } from './data';

const logger = Logger('GenerateDrawingToolbar');

export interface GenerateDrawingToolbarRef {
    focus: () => void;
}

/**
 * Toolbar with input to generate a drawing.
 */
export const GenerateDrawingToolbar = React.forwardRef(function GenerateDrawingToolbar(
    props: {
        /** Key of the drawing block */
        nodeKey: string;

        /** Maximum of suggestions to display */
        maxSuggestions?: number;

        /** Callback when drawing has been generated */
        onSubmitDrawing: (drawing: DrawingDocumentData) => void | Promise<void>;

        /** Callback when the user is closing the toolbar, only displays a button if defined */
        onClose?: () => void;

        /** Where to position the button */
        closeDirection?: 'top' | 'bottom';

        /** For analytics */
        analyticsFrom: 'empty' | 'editor';
    },
    ref: React.Ref<GenerateDrawingToolbarRef>
) {
    const {
        nodeKey,
        maxSuggestions = 4,
        onSubmitDrawing,
        closeDirection = 'top',
        onClose,
        analyticsFrom,
        ...rest
    } = props;

    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const hiveWorkingDocument = useHiveWorkingDocument();
    assertIsDefined(hiveWorkingDocument);

    const inputRef = React.useRef<TextInputRef>(null);
    const [text, setText] = React.useState('');
    const [loading, wrapLoading] = useLoadingState();
    const [suggestions, setSuggestions] = React.useState<string[]>([]);

    React.useImperativeHandle(
        ref,
        () => ({
            focus: () => {
                inputRef.current?.focus();
            },
        }),
        [inputRef]
    );

    React.useEffect(() => {
        hiveWorkingDocument.completions.generateDiagramSuggestions({ nodeKey }).then(
            (result) => {
                setSuggestions(result.suggestions.slice(0, maxSuggestions));
            },
            (error) => {
                logger.error('error', error);
            }
        );
    }, [hiveWorkingDocument, maxSuggestions]);

    const onSubmit = (submittedText: string = text, isSuggestion = false) => {
        if (!submittedText) {
            return;
        }

        if (text !== submittedText) {
            setText(submittedText);
        }

        wrapLoading(async () => {
            const result = await hiveWorkingDocument.completions.generateDiagram({
                nodeKey,
                prompt: text,
            });

            const drawing = result ? await createDrawingFromMermaid(result.mermaid) : null;
            if (drawing) {
                await onSubmitDrawing(drawing);

                // Then refocus the input to allow the user to continue typing
                inputRef.current?.focus();
            }

            logAnalyticsEvent({
                type: 'edit_drawing_generate',
                from: analyticsFrom,
                prompt: text,
                isSuggestion,
                successGeneration: !!result,
                successParsing: !!drawing,
            });
        });
    };

    const closeButton = onClose ? (
        <Button
            kind="nested"
            leadingIcon={closeDirection === 'bottom' ? Icon.ChevronDown : Icon.ChevronUp}
            iconOnly
            onPress={onClose}
            label="Close"
        />
    ) : null;

    return (
        <div
            {...rest}
            className="flex w-full flex-1 flex-col"
            onMouseDown={(event) => {
                // When the toolbar is within the block where double clicking opens the modal
                event.stopPropagation();
            }}
            onClick={(event) => {
                // When the toolbar is within the block where double clicking opens the modal
                event.stopPropagation();
            }}
        >
            {suggestions.length > 0 ? (
                <div className="flex flex-wrap">
                    {suggestions.map((suggestion, index) => (
                        <Pill
                            key={index}
                            leadingIcon={Icon.Magic}
                            size="small"
                            label={suggestion}
                            className="mr-2 mb-2"
                            onPress={() => {
                                onSubmit(suggestion, true);
                            }}
                        />
                    ))}
                </div>
            ) : null}
            <div className="flex items-center rounded bg-muted p-2">
                <TextInput
                    ref={inputRef}
                    icon={Icon.Magic}
                    placeholder="Describe the diagram or drawing you want to create"
                    value={text}
                    onChangeText={setText}
                    style={flex.flex1}
                    onSubmit={() => onSubmit()}
                />
                <Button
                    kind="primary"
                    leadingIcon={loading ? LoadingIndicator : undefined}
                    trailingIcon={Icon.ArrowRight}
                    label="Generate"
                    onPress={() => onSubmit()}
                    className="ml-2"
                    disabled={loading}
                />
                <div className="ml-2">{closeButton}</div>
            </div>
        </div>
    );
});
